// src/pages/deposit/Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import './Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000/api';

const DepositDashboard = () => {
  const toast = useRef(null);
  
  // State
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [upcomingMaturities, setUpcomingMaturities] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ branches: [], years: [] });
  const [showMaturityDialog, setShowMaturityDialog] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [accountSummary, setAccountSummary] = useState(null);
  const [searchMemberId, setSearchMemberId] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    branchCode: '',
    year: new Date().getFullYear()
  });

  // Get auth token
  const getToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  // API call function
  const apiCall = async (endpoint, method = 'GET', data = null, params = null) => {
    try {
      const token = getToken();
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        ...(params && { params }),
        ...(data && { data })
      };
      
      const response = await axios(config);
      return response.data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await apiCall('dashboard/filter-options');
      if (response?.success) {
        setFilterOptions(response.data || { branches: [], years: [] });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Set default options if API fails
      setFilterOptions({ 
        branches: [], 
        years: [new Date().getFullYear(), new Date().getFullYear() - 1] 
      });
      return false;
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        branchCode: filters.branchCode
      };
      
      // Fetch all data in parallel with individual error handling
      const results = await Promise.allSettled([
        apiCall('dashboard/statistics', 'GET', null, params),
        apiCall('dashboard/trends', 'GET', null, { year: filters.year, branchCode: filters.branchCode }),
        apiCall('dashboard/branchwise', 'GET', null, params),
        apiCall('dashboard/insights', 'GET', null, { branchCode: filters.branchCode }),
        apiCall('dashboard/recent-transactions', 'GET', null, { limit: 10 }),
        apiCall('dashboard/upcoming-maturities', 'GET', null, { days: 30, branchCode: filters.branchCode })
      ]);

      // Process each result
      const [
        statsResult,
        trendsResult,
        branchResult,
        insightsResult,
        transactionsResult,
        maturitiesResult
      ] = results;

      // Set statistics
      if (statsResult.status === 'fulfilled' && statsResult.value?.success) {
        setStatistics(statsResult.value.data || null);
      } else {
        console.warn('Failed to load statistics');
        // Set default empty statistics to prevent errors
        setStatistics({
          overall: {},
          fixedDeposits: {},
          recurringDeposits: {},
          savingsAccounts: {},
          typeDistribution: []
        });
      }

      // Set trends
      if (trendsResult.status === 'fulfilled' && trendsResult.value?.success) {
        setTrends(trendsResult.value.data || []);
      } else {
        console.warn('Failed to load trends');
        setTrends([]);
      }

      // Set branch data
      if (branchResult.status === 'fulfilled' && branchResult.value?.success) {
        setBranchData(branchResult.value.data || []);
      } else {
        console.warn('Failed to load branch data');
        setBranchData([]);
      }

      // Set insights
      if (insightsResult.status === 'fulfilled' && insightsResult.value?.success) {
        setInsights(insightsResult.value.data || []);
      } else {
        console.warn('Failed to load insights');
        setInsights([]);
      }

      // Set recent transactions
      if (transactionsResult.status === 'fulfilled' && transactionsResult.value?.success) {
        setRecentTransactions(transactionsResult.value.data || []);
      } else {
        console.warn('Failed to load recent transactions');
        setRecentTransactions([]);
      }

      // Set upcoming maturities
      if (maturitiesResult.status === 'fulfilled' && maturitiesResult.value?.success) {
        setUpcomingMaturities(maturitiesResult.value.data || []);
      } else {
        console.warn('Failed to load upcoming maturities');
        setUpcomingMaturities([]);
      }

      setDataLoaded(true);
      setInitialLoading(false);
      
    } catch (error) {
      console.error('Dashboard error:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
      setInitialLoading(false);
      setDataLoaded(true);
      
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to fetch dashboard data',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize dashboard
  const initializeDashboard = async () => {
    setInitialLoading(true);
    setError(null);
    
    try {
      // First fetch filter options
      await fetchFilterOptions();
      
      // Then fetch dashboard data
      await fetchDashboardData();
    } catch (error) {
      console.error('Initialization error:', error);
      setError('Failed to initialize dashboard. Please try again.');
      setInitialLoading(false);
      setDataLoaded(true);
    }
  };

  // Load data on mount
  useEffect(() => {
    initializeDashboard();
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (dataLoaded && (filters.startDate || filters.endDate || filters.branchCode || filters.year)) {
      fetchDashboardData();
    }
  }, [filters.startDate, filters.endDate, filters.branchCode, filters.year]);

  // Search account
  const handleSearchAccount = async () => {
    if (!searchMemberId.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter a Member ID',
        life: 3000
      });
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiCall(`dashboard/account-summary/${searchMemberId}`);
      if (response?.success) {
        setAccountSummary(response.data);
        setShowAccountDialog(true);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response?.message || 'Member not found',
          life: 3000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Member not found',
        life: 3000
      });
    } finally {
      setSearchLoading(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchDashboardData();
    toast.current?.show({
      severity: 'info',
      summary: 'Refreshing',
      detail: 'Dashboard data is being refreshed...',
      life: 2000
    });
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // KPI Card Component
  const KPICard = ({ title, value, subtitle, icon, color, trend, trendValue, delay }) => {
    const isPositive = trend === 'up';
    
    return (
      <div className="kpi-card" style={{ animationDelay: `${delay}ms` }}>
        <div className="kpi-card-inner" style={{ background: color.gradient, boxShadow: color.shadow }}>
          <div className="kpi-card-header">
            <div className="kpi-icon-wrapper">
              <i className={`pi ${icon} kpi-icon`}></i>
            </div>
            {trend && (
              <div className="kpi-trend">
                <span className={`trend-badge ${isPositive ? 'trend-up' : 'trend-down'}`}>
                  <i className={`pi ${isPositive ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
                  {trendValue}%
                </span>
              </div>
            )}
          </div>
          <div className="kpi-card-body">
            <div className="kpi-value">{value || '0'}</div>
            <div className="kpi-title">{title}</div>
            {subtitle && <div className="kpi-subtitle">{subtitle}</div>}
          </div>
          <div className="kpi-card-footer">
            <div className="kpi-progress">
              <div className="kpi-progress-bar" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render KPI Cards
  const renderKPICards = () => {
    if (!statistics) {
      // Show skeleton loading
      return (
        <div className="kpi-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="kpi-skeleton">
              <Skeleton width="100%" height="100px" borderRadius="16px" />
            </Card>
          ))}
        </div>
      );
    }

    const { overall, fixedDeposits, recurringDeposits, savingsAccounts } = statistics;

    const cardColors = [
      { gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', shadow: '0 10px 30px rgba(102, 126, 234, 0.4)' },
      { gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', shadow: '0 10px 30px rgba(245, 87, 108, 0.4)' },
      { gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', shadow: '0 10px 30px rgba(79, 172, 254, 0.4)' },
      { gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', shadow: '0 10px 30px rgba(67, 233, 123, 0.4)' },
      { gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', shadow: '0 10px 30px rgba(250, 112, 154, 0.4)' },
      { gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', shadow: '0 10px 30px rgba(161, 140, 209, 0.4)' }
    ];

    const kpiData = [
      {
        title: 'Total Deposits',
        value: formatCurrency(overall?.totalDepositAmount),
        subtitle: `${overall?.totalDepositAccounts || 0} Accounts`,
        icon: 'pi-chart-line',
        color: cardColors[0],
        trend: 'up',
        trendValue: '12.5',
        delay: 0
      },
      {
        title: 'Fixed Deposits',
        value: formatCurrency(fixedDeposits?.totalAmount),
        subtitle: `${fixedDeposits?.activeAccounts || 0} Active`,
        icon: 'pi-money-bill',
        color: cardColors[1],
        trend: 'up',
        trendValue: '8.3',
        delay: 100
      },
      {
        title: 'Recurring Deposits',
        value: formatCurrency(recurringDeposits?.totalAmount),
        subtitle: `${recurringDeposits?.activeAccounts || 0} Active`,
        icon: 'pi-calendar',
        color: cardColors[2],
        trend: 'down',
        trendValue: '2.1',
        delay: 200
      },
      {
        title: 'Savings Balance',
        value: formatCurrency(savingsAccounts?.totalBalance),
        subtitle: `${savingsAccounts?.totalAccounts || 0} Accounts`,
        icon: 'pi-credit-card',
        color: cardColors[3],
        trend: 'up',
        trendValue: '15.7',
        delay: 300
      },
      {
        title: 'Interest Earned',
        value: formatCurrency(overall?.totalInterestEarned),
        subtitle: 'Total Interest',
        icon: 'pi-percentage',
        color: cardColors[4],
        trend: 'up',
        trendValue: '5.2',
        delay: 400
      },
      {
        title: 'Active Accounts',
        value: `${overall?.activeAccounts || 0}`,
        subtitle: 'Total Active',
        icon: 'pi-users',
        color: cardColors[5],
        trend: 'up',
        trendValue: '10.8',
        delay: 500
      }
    ];

    return (
      <div className="kpi-grid">
        {kpiData.map((kpi, index) => (
          <KPICard key={index} {...kpi} />
        ))}
      </div>
    );
  };

  // Chart 1: Deposit Distribution
  const renderDepositDistributionChart = () => {
    const hasData = statistics?.typeDistribution && statistics.typeDistribution.length > 0;

    const data = {
      labels: hasData ? statistics.typeDistribution.map(d => d.depositeType || 'Unknown') : ['No Data'],
      datasets: [
        {
          data: hasData ? statistics.typeDistribution.map(d => d.totalAmount || 0) : [1],
          backgroundColor: hasData ? ['#667eea', '#f5576c', '#4facfe', '#43e97b'] : ['#e0e0e0'],
          borderColor: hasData ? ['#667eea', '#f5576c', '#4facfe', '#43e97b'] : ['#e0e0e0'],
          borderWidth: 3,
          hoverOffset: 15
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
            pointStyle: 'circle',
            font: { size: 13, weight: '500' }
          }
        }
      },
      cutout: '65%'
    };

    return (
      <div className="chart-wrapper">
        <Card className="chart-card">
          <div className="chart-header">
            <h3>📊 Deposit Distribution</h3>
            <Tag value={hasData ? 'Current' : 'No Data'} severity={hasData ? 'info' : 'warning'} rounded />
          </div>
          <div style={{ height: '320px' }}>
            <Chart type="doughnut" data={data} options={options} />
          </div>
        </Card>
      </div>
    );
  };

  // Chart 2: Monthly Trends
  const renderMonthlyTrendsChart = () => {
    const hasData = trends && trends.length > 0;

    const data = {
      labels: hasData ? trends.map(t => t.month || '') : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          label: 'Fixed Deposits',
          data: hasData ? trends.map(t => t.fixedDepositAmount || 0) : [0, 0, 0, 0, 0, 0],
          borderColor: '#667eea',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#667eea',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 8
        },
        {
          label: 'Recurring Deposits',
          data: hasData ? trends.map(t => t.recurringDepositAmount || 0) : [0, 0, 0, 0, 0, 0],
          borderColor: '#f5576c',
          backgroundColor: 'rgba(245, 87, 108, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#f5576c',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 8
        },
        {
          label: 'Savings Balance',
          data: hasData ? trends.map(t => t.savingsBalance || 0) : [0, 0, 0, 0, 0, 0],
          borderColor: '#4facfe',
          backgroundColor: 'rgba(79, 172, 254, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#4facfe',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 8
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
            pointStyle: 'circle',
            font: { size: 12, weight: '500' }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
            drawBorder: false
          },
          ticks: {
            callback: function(value) {
              return '₹' + value.toLocaleString();
            }
          }
        },
        x: {
          grid: { display: false }
        }
      }
    };

    return (
      <div className="chart-wrapper full-width">
        <Card className="chart-card">
          <div className="chart-header">
            <h3>📈 Monthly Performance Trends</h3>
            <Tag value={hasData ? 'Live' : 'No Data'} severity={hasData ? 'success' : 'warning'} rounded />
          </div>
          <div style={{ height: '350px' }}>
            <Chart type="line" data={data} options={options} />
          </div>
        </Card>
      </div>
    );
  };

  // Chart 3: Branch Performance
  const renderBranchPerformanceChart = () => {
    const hasData = branchData && branchData.length > 0;

    const data = {
      labels: hasData ? branchData.map(b => b.branchName || b.branchCode) : ['No Branches'],
      datasets: [
        {
          label: 'Fixed Deposits',
          data: hasData ? branchData.map(b => b.fixedDepositAmount || 0) : [0],
          backgroundColor: 'rgba(102, 126, 234, 0.8)',
          borderColor: '#667eea',
          borderWidth: 2,
          borderRadius: 8
        },
        {
          label: 'Recurring Deposits',
          data: hasData ? branchData.map(b => b.recurringDepositAmount || 0) : [0],
          backgroundColor: 'rgba(245, 87, 108, 0.8)',
          borderColor: '#f5576c',
          borderWidth: 2,
          borderRadius: 8
        },
        {
          label: 'Savings Balance',
          data: hasData ? branchData.map(b => b.savingsBalance || 0) : [0],
          backgroundColor: 'rgba(79, 172, 254, 0.8)',
          borderColor: '#4facfe',
          borderWidth: 2,
          borderRadius: 8
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
            pointStyle: 'circle',
            font: { size: 12, weight: '500' }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
            drawBorder: false
          },
          ticks: {
            callback: function(value) {
              return '₹' + value.toLocaleString();
            }
          }
        },
        x: {
          grid: { display: false }
        }
      }
    };

    return (
      <div className="chart-wrapper full-width">
        <Card className="chart-card">
          <div className="chart-header">
            <h3>🏢 Branch Performance Comparison</h3>
            <Tag value={hasData ? 'Current' : 'No Data'} severity={hasData ? 'warning' : 'warning'} rounded />
          </div>
          <div style={{ height: '350px' }}>
            <Chart type="bar" data={data} options={options} />
          </div>
        </Card>
      </div>
    );
  };

  // Render Insights
  const renderInsights = () => {
    if (!insights || insights.length === 0) {
      return (
        <div className="insights-section">
          <Card className="insights-card">
            <div className="insights-header">
              <h3>💡 Key Insights</h3>
            </div>
            <div className="no-data">No insights available</div>
          </Card>
        </div>
      );
    }

    return (
      <div className="insights-section">
        <Card className="insights-card">
          <div className="insights-header">
            <h3>💡 Key Insights</h3>
            <Badge value={`${insights.length} insights`} severity="info" />
          </div>
          <div className="insights-grid">
            {insights.map((insight, index) => (
              <div key={index} className="insight-item">
                <div className="insight-icon">{insight.icon || '📊'}</div>
                <div className="insight-content">
                  <h4>{insight.title}</h4>
                  <p>{insight.description}</p>
                  {insight.trend && (
                    <span className={`insight-trend ${insight.trend === 'up' ? 'trend-up' : 'trend-down'}`}>
                      <i className={`pi ${insight.trend === 'up' ? 'pi-arrow-up' : 'pi-arrow-down'}`}></i>
                      {insight.trendValue?.toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  // Render Recent Transactions
  const renderRecentTransactions = () => {
    if (!recentTransactions || recentTransactions.length === 0) {
      return (
        <Card className="transactions-card">
          <div className="transactions-header">
            <h3>🔄 Recent Transactions</h3>
            <Badge value="0 entries" severity="info" />
          </div>
          <div className="no-data">No recent transactions found</div>
        </Card>
      );
    }

    const amountTemplate = (rowData) => {
      const amount = rowData.debit || rowData.credit || 0;
      const isDebit = rowData.debit > 0;
      return (
        <span style={{ color: isDebit ? '#f5576c' : '#43e97b', fontWeight: '600' }}>
          {isDebit ? '−' : '+'} {formatCurrency(amount)}
        </span>
      );
    };

    const statusTemplate = (rowData) => {
      const status = rowData.status || 'Completed';
      const severity = status === 'Completed' ? 'success' : status === 'Pending' ? 'warning' : 'info';
      return <Tag value={status} severity={severity} rounded />;
    };

    return (
      <Card className="transactions-card">
        <div className="transactions-header">
          <h3>🔄 Recent Transactions</h3>
          <Badge value={`${recentTransactions.length} entries`} severity="info" />
        </div>
        <DataTable 
          value={recentTransactions} 
          className="p-datatable-sm" 
          responsiveLayout="scroll"
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 20]}
        >
          <Column field="policyno" header="Policy No" sortable style={{ minWidth: '120px' }} />
          <Column field="TransactionDate" header="Date" body={(row) => formatDate(row.TransactionDate)} sortable />
          <Column field="remarks" header="Remarks" style={{ minWidth: '150px' }} />
          <Column field="debit" header="Amount" body={amountTemplate} sortable />
          <Column field="branchcode" header="Branch" style={{ minWidth: '100px' }} />
          <Column field="status" header="Status" body={statusTemplate} style={{ minWidth: '100px' }} />
        </DataTable>
      </Card>
    );
  };

  // Render Upcoming Maturities
  const renderUpcomingMaturities = () => {
    if (!upcomingMaturities || upcomingMaturities.length === 0) return null;

    return (
      <>
        <div className="maturity-summary" onClick={() => setShowMaturityDialog(true)}>
          <Card className="maturity-card">
            <div className="maturity-content">
              <div className="maturity-icon">⏰</div>
              <div>
                <div className="maturity-count">{upcomingMaturities.length}</div>
                <div className="maturity-label">Deposits maturing in 30 days</div>
              </div>
              <div className="maturity-arrow-wrapper">
                <i className="pi pi-chevron-right maturity-arrow"></i>
              </div>
            </div>
          </Card>
        </div>

        <Dialog 
          header="📋 Upcoming Maturities" 
          visible={showMaturityDialog} 
          onHide={() => setShowMaturityDialog(false)}
          style={{ width: '85%', maxWidth: '1200px' }}
          className="maturity-dialog"
        >
          <DataTable 
            value={upcomingMaturities} 
            className="p-datatable-sm" 
            responsiveLayout="scroll"
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            emptyMessage="No upcoming maturities"
          >
            <Column field="policyno" header="Policy No" sortable />
            <Column field="deposite_type" header="Type" sortable />
            <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} sortable />
            <Column field="maturityamount" header="Maturity Amount" body={(row) => formatCurrency(row.maturityamount)} sortable />
            <Column field="maturitydate" header="Maturity Date" body={(row) => formatDate(row.maturitydate)} sortable />
            <Column field="branchcode" header="Branch" />
            <Column field="interest" header="Interest" body={(row) => formatCurrency(row.interest)} sortable />
          </DataTable>
        </Dialog>
      </>
    );
  };

  // Account Search Dialog
  const renderAccountSearch = () => {
    return (
      <>
        <div className="account-search">
          <div className="search-container">
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText 
                placeholder="Search Member ID" 
                value={searchMemberId}
                onChange={(e) => setSearchMemberId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchAccount()}
                className="search-input"
              />
            </span>
            <Button 
              icon="pi pi-search" 
              label="Search" 
              onClick={handleSearchAccount}
              className="search-button"
              loading={searchLoading}
            />
          </div>
        </div>

        <Dialog 
          header="👤 Account Summary" 
          visible={showAccountDialog} 
          onHide={() => setShowAccountDialog(false)}
          style={{ width: '80%', maxWidth: '1100px' }}
          className="account-dialog"
        >
          {accountSummary && (
            <div className="account-summary">
              <div className="summary-header">
                <div className="summary-user">
                  <i className="pi pi-user-circle" style={{ fontSize: '2.5rem', color: '#667eea' }}></i>
                  <div>
                    <h3>Member: {accountSummary.memberId}</h3>
                    <p className="summary-subtitle">
                      {accountSummary.savingsAccount?.membername || 'Member'}
                    </p>
                  </div>
                </div>
                <Tag value={`${accountSummary.activeDeposits || 0} Active Deposits`} severity="success" />
              </div>
              
              <div className="summary-stats">
                <div className="stat-box">
                  <div className="stat-label">Total Deposits</div>
                  <div className="stat-value">{accountSummary.totalDeposits || 0}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Total Deposit Amount</div>
                  <div className="stat-value">{formatCurrency(accountSummary.totalDepositAmount)}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Total Maturity Amount</div>
                  <div className="stat-value">{formatCurrency(accountSummary.totalMaturityAmount)}</div>
                </div>
                <div className="stat-box">
                  <div className="stat-label">Savings Balance</div>
                  <div className="stat-value">{formatCurrency(accountSummary.savingsAccount?.currentBalance)}</div>
                </div>
              </div>

              {accountSummary.savingsAccount && (
                <div className="savings-details">
                  <h4>🏦 Savings Account Details</h4>
                  <div className="details-grid">
                    <div><strong>Account No:</strong> {accountSummary.savingsAccount.accountnumber}</div>
                    <div><strong>Member Name:</strong> {accountSummary.savingsAccount.membername}</div>
                    <div><strong>Mobile:</strong> {accountSummary.savingsAccount.mobile}</div>
                    <div><strong>Email:</strong> {accountSummary.savingsAccount.emailid || '-'}</div>
                    <div><strong>Branch:</strong> {accountSummary.savingsAccount.branchcode}</div>
                    <div><strong>Open Date:</strong> {formatDate(accountSummary.savingsAccount.accountopen_date)}</div>
                    <div><strong>Available Balance:</strong> {formatCurrency(accountSummary.savingsAccount.availableBalance)}</div>
                    <div><strong>Documents:</strong> {accountSummary.savingsAccount.documents_ok ? '✅ Verified' : '⏳ Pending'}</div>
                  </div>
                </div>
              )}

              {accountSummary.depositDetails && accountSummary.depositDetails.length > 0 && (
                <div className="deposit-details">
                  <h4>📊 Deposit Details</h4>
                  <DataTable value={accountSummary.depositDetails} className="p-datatable-sm" paginator rows={5}>
                    <Column field="policyno" header="Policy No" />
                    <Column field="deposite_type" header="Type" />
                    <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                    <Column field="maturityamount" header="Maturity" body={(row) => formatCurrency(row.maturityamount)} />
                    <Column field="term_period" header="Term" body={(row) => `${row.term_period} Years`} />
                    <Column field="interest" header="Interest" body={(row) => formatCurrency(row.interest)} />
                    <Column field="ac_closed" header="Status" body={(row) => (
                      <Tag value={row.ac_closed} severity={row.ac_closed === 'ACTIVE' ? 'success' : 'danger'} rounded />
                    )} />
                  </DataTable>
                </div>
              )}
            </div>
          )}
        </Dialog>
      </>
    );
  };

  // Render Filter Bar
  const renderFilterBar = () => {
    const branchOptions = [
      { label: 'All Branches', value: '' },
      ...(filterOptions.branches || []).map(b => ({ label: b.name, value: b.code }))
    ];

    const yearOptions = (filterOptions.years || []).map(y => ({ label: String(y), value: y }));

    return (
      <div className="filter-bar">
        <div className="filter-left">
          <div className="filter-title">
            <i className="pi pi-sliders-h"></i>
            <span>Filters</span>
          </div>
        </div>
        <div className="filter-right">
          <div className="filter-group">
            <Calendar
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.value })}
              showIcon
              placeholder="Start Date"
              className="filter-calendar"
              dateFormat="dd/mm/yy"
            />
          </div>
          <div className="filter-group">
            <Calendar
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.value })}
              showIcon
              placeholder="End Date"
              className="filter-calendar"
              dateFormat="dd/mm/yy"
            />
          </div>
          <div className="filter-group">
            <Dropdown
              value={filters.year}
              options={yearOptions}
              onChange={(e) => setFilters({ ...filters, year: e.value })}
              placeholder="Year"
              className="filter-dropdown"
            />
          </div>
          <div className="filter-group">
            <Dropdown
              value={filters.branchCode}
              options={branchOptions}
              onChange={(e) => setFilters({ ...filters, branchCode: e.value })}
              placeholder="Branch"
              className="filter-dropdown"
            />
          </div>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            className="refresh-button"
            onClick={handleRefresh}
            loading={loading}
          />
        </div>
      </div>
    );
  };

  // Loading state
  if (initialLoading) {
    return (
      <div className="dashboard-wrapper">
        <div className="loading-container">
          <div className="loading-spinner-wrapper">
            <ProgressSpinner style={{ width: '70px', height: '70px' }} strokeWidth="4" />
          </div>
          <h2 className="loading-title">Loading Dashboard...</h2>
          <p className="loading-subtext">Please wait while we fetch your data</p>
          <div className="loading-progress">
            <div className="loading-progress-bar"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-wrapper">
        <div className="error-container">
          <div className="error-icon-wrapper">
            <i className="pi pi-exclamation-triangle"></i>
          </div>
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <div className="error-actions">
            <Button 
              label="Try Again" 
              icon="pi pi-refresh" 
              className="retry-button"
              onClick={handleRefresh}
              loading={loading}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Toast ref={toast} />
      
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-top">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="pi pi-chart-bar header-icon"></i>
              </div>
              <div>
                <h1 className="dashboard-title">Deposit Dashboard</h1>
                <p className="dashboard-subtitle">Real-time overview of all deposit accounts</p>
              </div>
            </div>
            <div className="header-right">
              {renderAccountSearch()}
              <div className="header-status">
                <Tag value="Live" severity="success" rounded className="status-tag" />
                <Badge value="Updated" severity="info" className="update-badge" />
              </div>
            </div>
          </div>
          
          <div className="header-bottom">
            <div className="header-stats">
              <div className="stat-item">
                <i className="pi pi-calendar"></i>
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              <span className="stat-divider">|</span>
              <div className="stat-item">
                <i className="pi pi-bell"></i>
                {upcomingMaturities?.length || 0} Maturities
              </div>
              <span className="stat-divider">|</span>
              <div className="stat-item">
                <i className="pi pi-database"></i>
                {statistics?.overall?.totalDepositAccounts || 0} Total Accounts
              </div>
            </div>
          </div>
          
          {renderFilterBar()}
        </div>

        {/* KPI Cards */}
        {renderKPICards()}

        {/* Upcoming Maturities */}
        {renderUpcomingMaturities()}

        {/* Charts */}
        <TabView 
          className="chart-tabs" 
          activeIndex={activeTab} 
          onTabChange={(e) => setActiveTab(e.index)}
        >
          <TabPanel header="📊 Overview">
            <div className="charts-grid">
              {renderDepositDistributionChart()}
              {renderMonthlyTrendsChart()}
              {renderBranchPerformanceChart()}
            </div>
          </TabPanel>
          
          <TabPanel header="📈 Transactions">
            {renderRecentTransactions()}
          </TabPanel>
        </TabView>

        {/* Insights */}
        {renderInsights()}

        {/* Footer */}
        <div className="dashboard-footer">
          <span>© {new Date().getFullYear()} MicrofinCloud. All rights reserved.</span>
          <span className="footer-divider">|</span>
          <span>Data refreshed: {new Date().toLocaleTimeString('en-IN')}</span>
        </div>
      </div>
    </div>
  );
};

export default DepositDashboard;